import { HttpErrorResponse } from '@angular/common/http';
import { DomainError } from './domain-error';
import {
  IApiErrorResponse,
  IServiceException,
} from '../../interfaces/api-error.interface';
import {
  GENERAL_ERROR_CODE,
  UNEXPECTED_ERROR_CODE,
  NETWORK_ERROR_CODE,
  UNEXPECTED_ERROR_MESSAGE,
  NETWORK_ERROR_MESSAGE,
  GENERAL_ERROR_MESSAGE,
  TECHNICAL_ERROR_CODES,
} from '../../constants/error-http.constants';

const isHttpError = (error: unknown): error is HttpErrorResponse =>
  error instanceof HttpErrorResponse;

const isGeneralError = (error: unknown): error is Error =>
  typeof error === 'object' && error !== null && 'message' in error;

const isDomainError = (error: unknown): error is DomainError =>
  error instanceof DomainError;

export class HttpErrorParser {
  static parse(error: unknown, fallbackMessage?: string): IServiceException {
    if (isDomainError(error)) {
      return {
        code: error.code,
        message: error.getFullMessage(),
        details: error.details,
      };
    }

    if (isHttpError(error) && error.error) {
      const backendError = error.error as IApiErrorResponse;
      const code = this.extractCode(backendError);
      const message = this.extractMessage(backendError);
      const details = backendError.data;

      return { code, message, details };
    }

    if (isHttpError(error) && error.status === 0) {
      return {
        code: NETWORK_ERROR_CODE,
        message: NETWORK_ERROR_MESSAGE,
      };
    }

    if (isGeneralError(error) && error.message) {
      return {
        code: GENERAL_ERROR_CODE,
        message: error.message,
      };
    }

    if (fallbackMessage) {
      return {
        code: GENERAL_ERROR_CODE,
        message: fallbackMessage,
      };
    }

    return {
      code: UNEXPECTED_ERROR_CODE,
      message: UNEXPECTED_ERROR_MESSAGE,
    };
  }

  static parseHttpError(httpError: HttpErrorResponse): DomainError {
    if (httpError.status === 0) {
      return new DomainError(NETWORK_ERROR_CODE, NETWORK_ERROR_MESSAGE);
    }

    const backendError = httpError.error as IApiErrorResponse;
    const code = this.extractCode(backendError);
    const message = this.extractMessage(backendError);
    const details = backendError?.data;

    return new DomainError(code, message, details);
  }

  private static extractCode(backendError: IApiErrorResponse | null): string {
    if (!backendError) {
      return UNEXPECTED_ERROR_CODE;
    }

    if (backendError.code && !/^\d+$/.test(backendError.code)) {
      return backendError.code;
    }

    if (backendError.statusCode) {
      return `HTTP_${backendError.statusCode}`;
    }

    return GENERAL_ERROR_CODE;
  }

  private static extractMessage(
    backendError: IApiErrorResponse | null,
  ): string {
    if (!backendError) {
      return GENERAL_ERROR_MESSAGE;
    }

    const code = this.extractCode(backendError);
    if (this.isTechnicalError(code)) {
      return GENERAL_ERROR_MESSAGE;
    }

    return (
      backendError.message ||
      backendError.description ||
      backendError.error ||
      GENERAL_ERROR_MESSAGE
    );
  }

  private static isTechnicalError(code: string): boolean {
    return TECHNICAL_ERROR_CODES.includes(code);
  }

  static hasDetails(error: unknown): boolean {
    if (isDomainError(error)) {
      return error.hasDetails();
    }
    return false;
  }

  static getDetails(error: unknown): string[] {
    if (isDomainError(error)) {
      return error.getDetails();
    }
    return [];
  }
}
